import { motion } from 'framer-motion';
import { ShoppingCart, ShoppingBag, ArrowRight } from 'lucide-react';

const AMAZON_LINK = 'https://www.amazon.com/Divine-Intervention-Journal-Gratitude-Journey/dp/B0GTLJ2LD1';
const SHOPIFY_LINK = 'https://cuavena.myshopify.com';
const JOURNAL_COVER = 'https://customer-assets.emergentagent.com/job_faith-reads-3/artifacts/sfard4sy_886530C1-7F9C-45C7-83B3-BC93BEC6A426.png';
const HERO_BG = 'https://images.unsplash.com/photo-1660231839492-a2eb20bcede3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxnb2xkZW4lMjBzdW5saWdodCUyMHRocm91Z2glMjBjbG91ZHMlMjBkaXZpbmUlMjBzcGlyaXR1YWx8ZW58MHx8fHwxNzc2MTk5NDM4fDA&ixlib=rb-4.1.0&q=85';

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

export default function HomePage() {
  return (
    <div data-testid="home-page">
      {/* ===== HERO SECTION ===== */}
      <section className="relative min-h-[60vh] md:min-h-[70vh] flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#2A2421]/75 via-[#2A2421]/50 to-[#FDFBF7]" />
        </div>
        <div className="relative z-10 text-center max-w-3xl mx-auto px-6 py-16">
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="font-sans text-xs sm:text-sm tracking-[0.35em] uppercase text-[#C59B76] mb-5"
          >
            Led By Faith Studios
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.1] mb-5 text-white"
            style={{ textShadow: '0 2px 20px rgba(0,0,0,0.3)' }}
          >
            Faith-Centered Books,<br className="hidden sm:block" /> Journals &amp; Products
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="font-sans text-base md:text-lg text-white/70 max-w-xl mx-auto mb-8 leading-relaxed"
          >
            Stories and tools rooted in resilience, gratitude, and encouragement through life's storms.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.6 }}
            className="flex flex-col sm:flex-row gap-3 justify-center"
          >
            <a
              href={SHOPIFY_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-shop-btn"
              className="inline-flex items-center justify-center gap-2 bg-[#A67A5B] text-white px-8 py-3.5 rounded-full font-medium tracking-wide shadow-lg hover:bg-[#8C654A] hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
            >
              <ShoppingBag size={18} />
              Shop on Shopify
            </a>
            <a
              href={AMAZON_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-amazon-btn"
              className="inline-flex items-center justify-center gap-2 border-2 border-white/30 text-white px-8 py-3.5 rounded-full font-medium tracking-wide hover:bg-white/10 hover:border-white/50 transition-all duration-300"
            >
              <ShoppingCart size={18} />
              Buy on Amazon
            </a>
          </motion.div>
        </div>
      </section>

      {/* ===== JOURNAL FEATURE — OUT NOW ===== */}
      <section className="py-16 md:py-24 bg-[#FDFBF7]" data-testid="journal-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
            {/* Journal Cover */}
            <motion.div {...fadeUp} className="flex justify-center md:justify-end">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-[#C59B76]/20 to-transparent rounded-3xl" />
                <img
                  src={JOURNAL_COVER}
                  alt="Divine Intervention - A 90-Day Faith & Gratitude Journal"
                  data-testid="journal-cover-image"
                  className="relative rounded-2xl shadow-2xl w-full max-w-[320px] md:max-w-[360px]"
                />
              </div>
            </motion.div>

            {/* Journal Info */}
            <motion.div {...fadeUp} className="text-left">
              <span className="inline-block bg-[#A67A5B] text-white text-xs font-semibold tracking-[0.2em] uppercase px-4 py-1.5 rounded-full mb-5">
                Out Now
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-[#2A2421] mb-2 leading-tight">
                Divine Intervention
              </h2>
              <p className="font-serif text-lg text-[#A67A5B] italic mb-5">
                A 90-Day Faith &amp; Gratitude Journey
              </p>
              <p className="font-sans text-base text-[#7A736E] leading-relaxed mb-8">
                Draw closer to God through daily reflection, prayer, and gratitude. This journal guides you through 90 days of intentional faith-building, helping you find peace, clarity, and spiritual strength in every season of life.
              </p>

              {/* Buy Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <a
                  href={SHOPIFY_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="journal-buy-shopify-btn"
                  className="inline-flex items-center justify-center gap-2 bg-[#A67A5B] text-white px-7 py-3.5 rounded-full font-medium tracking-wide shadow-md hover:bg-[#8C654A] hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
                >
                  <ShoppingBag size={18} />
                  Buy on Shopify
                </a>
                <a
                  href={AMAZON_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="journal-buy-amazon-btn"
                  className="inline-flex items-center justify-center gap-2 border-2 border-[#A67A5B] text-[#A67A5B] px-7 py-3.5 rounded-full font-medium tracking-wide hover:bg-[#A67A5B] hover:text-white transition-all duration-300"
                >
                  <ShoppingCart size={18} />
                  Buy on Amazon
                </a>
              </div>
              <p className="font-sans text-xs text-[#7A736E]/40 flex items-center gap-1.5">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                Secure checkout on Amazon &amp; Shopify
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== EXPLORE MORE — QUICK LINKS ===== */}
      <section className="py-12 md:py-16 bg-white border-t border-[#E8E1D9]/50" data-testid="explore-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <a
              href="/about"
              data-testid="explore-about"
              className="group p-6 rounded-2xl border border-[#E8E1D9] hover:border-[#A67A5B]/30 hover:shadow-md transition-all duration-300 text-center"
            >
              <p className="font-serif text-lg font-semibold text-[#2A2421] mb-1 group-hover:text-[#A67A5B] transition-colors">About Us</p>
              <p className="font-sans text-xs text-[#7A736E]">Our story &amp; mission</p>
            </a>
            <a
              href="/gratitude-journal"
              data-testid="explore-journal"
              className="group p-6 rounded-2xl border border-[#E8E1D9] hover:border-[#A67A5B]/30 hover:shadow-md transition-all duration-300 text-center"
            >
              <p className="font-serif text-lg font-semibold text-[#2A2421] mb-1 group-hover:text-[#A67A5B] transition-colors">Books &amp; Projects</p>
              <p className="font-sans text-xs text-[#7A736E]">Journals, books &amp; more</p>
            </a>
            <a
              href="/contact"
              data-testid="explore-contact"
              className="group p-6 rounded-2xl border border-[#E8E1D9] hover:border-[#A67A5B]/30 hover:shadow-md transition-all duration-300 text-center"
            >
              <p className="font-serif text-lg font-semibold text-[#2A2421] mb-1 group-hover:text-[#A67A5B] transition-colors">Contact</p>
              <p className="font-sans text-xs text-[#7A736E]">Let's connect</p>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
